<template>
    <div
        id="Container"
        class="widget-container background--card new-card-layout"
    >
        <a
            href="#"
            @click.prevent="closeSelf"
            class="close"
        >
            <svg-rndr svg="close.svg" />
        </a>
        <div class="widget-contents DialogContent h-auto">
            <div class="widget-contents__inner DialogContentInner card card-new">
                <div class="card-header">
                    <div class="form-row justify-content-center">
                        <div class="col-md-auto py-4">
                            <h1 v-translate>Personal Information</h1>
                        </div>
                    </div>
                </div>

                <div
                    class="card-body px-2"
                    :class="{
                        'perfect-scrollbar': hasScroll,
                        'px-sm-0': !isMobile
                    }"
                >
                    <form
                        ref="form"
                        method="POST"
                        @submit.prevent="handleForm"
                        enctype="multipart/form-data"
                        novalidate
                    >
                        <p
                            v-if="user.edit_lock"
                            class="mt-2 text-center"
                            v-translate
                        >
                            Some of your personal details can no longer be edited from here. If you want to change any of these please get in touch.
                        </p>
                        <div class="form-section">
                            <div class="form-group justify-content-center align-items-start form-row">
                                <label
                                    for="salutation"
                                    class="col-md-3"
                                    v-translate
                                >Title</label>

                                <div
                                    class="col-md-8 col-lg-7"
                                    v-if="Object.keys(allowedSalutations).length"
                                >
                                    <div
                                        class="tabs tabs--nowrap"
                                        :class="{ 'tabs-invalid': errors.has('salutation'), 'tabs-disabled': formSubmitting }"
                                    >
                                        <template v-for="(val, key, ind) in allowedSalutations">
                                            <input
                                                v-if="ind === 0"
                                                v-validate="'radioTabs'"
                                                data-vv-validate-on="change"
                                                :key="`input${ind}`"
                                                :id="`salutation_${key}`"
                                                type="radio"
                                                class="form-control"
                                                name="salutation"
                                                :disabled="formSubmitting ? true : false"
                                                required
                                                v-model="salutation"
                                                :value="key"
                                                :checked="salutation == key"
                                            >
                                            <input
                                                v-else
                                                data-vv-validate-on="change"
                                                :key="`input${ind}`"
                                                :id="`salutation_${key}`"
                                                type="radio"
                                                class="form-control"
                                                name="salutation"
                                                :disabled="formSubmitting ? true : false"
                                                required
                                                v-model="salutation"
                                                :value="key"
                                                :checked="salutation == key"
                                            >
                                            <label
                                                :key="`label${ind}`"
                                                class="tab"
                                                :for="`salutation_${key}`"
                                            >{{ val }}</label>
                                        </template>
                                    </div>
                                    <InlineError
                                        :errors="errors"
                                        type="salutation"
                                    />
                                </div>
                                <div
                                    class="col-md-8 col-lg-7 loader loader--inline"
                                    v-else
                                >
                                    <loader />
                                </div>
                            </div>

                            <div class="form-group justify-content-center align-items-start form-row">
                                <label
                                    for="first_name"
                                    class="col-md-3"
                                    v-translate
                                >First Name</label>

                                <div class="col-md-8 col-lg-7">
                                    <Input
                                        :rule="'required|name'"
                                        :validateAs="'first name' | translate"
                                        name="first_name"
                                        errorType="first_name"
                                        model="user.first_name"
                                        module="Widget"
                                        :required="true"
                                        :disabledCheck="!!user.edit_lock"
                                    />
                                </div>
                            </div>
                            <div class="form-group justify-content-center align-items-start form-row">
                                <label
                                    for="last_name"
                                    class="col-md-3"
                                    v-translate
                                >Last Name</label>

                                <div class="col-md-8 col-lg-7">
                                    <Input
                                        :rule="'required|name'"
                                        :validateAs="'last name' | translate"
                                        name="last_name"
                                        errorType="last_name"
                                        model="user.last_name"
                                        module="Widget"
                                        :required="true"
                                        :disabledCheck="!!user.edit_lock"
                                    />
                                </div>
                            </div>
                            <div class="form-group justify-content-center align-items-start form-row">
                                <label
                                    for="experience"
                                    class="col-md-3"
                                    v-translate
                                >Experience</label>
                                <div class="col-md-8 col-lg-7">
                                    <div
                                        class="tabs"
                                        :class="{ 'tabs-invalid': errors.has('experience'), 'tabs-disabled': formSubmitting }"
                                    >
                                        <input
                                            v-validate="'radioTabs'"
                                            data-vv-validate-on="change"
                                            id="experience_nonprof"
                                            type="radio"
                                            class="form-control"
                                            name="experience"
                                            :disabled="formSubmitting ? true : false"
                                            required
                                            v-model="experience"
                                            value="Non-Professional"
                                        >
                                        <label
                                            class="tab"
                                            for="experience_nonprof"
                                            v-translate
                                        >Non Professional</label>
                                        <input
                                            id="experience_prof"
                                            type="radio"
                                            class="form-control"
                                            name="experience"
                                            :disabled="formSubmitting ? true : false"
                                            required
                                            v-model="experience"
                                            value="Professional"
                                        >
                                        <label
                                            class="tab"
                                            for="experience_prof"
                                            v-translate
                                        >Professional</label>
                                    </div>
                                    <InlineError
                                        :errors="errors"
                                        type="experience"
                                    />
                                </div>
                            </div>
                            <div class="form-group justify-content-center align-items-start form-row">
                                <label
                                    for="dob-day"
                                    class="col-md-3"
                                    v-translate
                                >Date of Birth</label>

                                <DOBPicker
                                    :dobHandler="dobHandler"
                                    :months="months"
                                    :days="days"
                                    :user="user"
                                    :wrapperClass="'col-md-8 col-lg-7'"
                                    :disabledCheck="!!user.edit_lock"
                                />
                            </div>
                            <div class="form-group form-row justify-content-center align-items-start">
                                <label
                                    for="language"
                                    class="col-md-3"
                                    v-translate
                                >Language</label>

                                <div class="col-md-8 col-lg-7">
                                    <CustomSelect
                                        :options="$language.available"
                                        :label="'language'"
                                        :handler="languageHandler"
                                        :hasValue="currentLanguage"
                                    >
                                        <template slot-scope="{slotKey}">
                                            <span :class="`${$codes[slotKey] ? $codes[slotKey].class : null}`"></span>&nbsp;
                                        </template>
                                    </CustomSelect>
                                </div>
                            </div>
                            <div class="form-group justify-content-center align-items-start form-row">
                                <label
                                    for="nickname"
                                    class="col-md-3"
                                    v-translate
                                >Nickname</label>

                                <div class="col-md-8 col-lg-7">
                                    <Input
                                        name="nickname"
                                        errorType="nickname"
                                        model="user.nickname"
                                        module="Widget"
                                    />
                                </div>
                            </div>
                            <div class="form-group justify-content-center align-items-start form-row">
                                <label
                                    for="avatar"
                                    class="col-md-3"
                                    v-translate
                                >Profile Picture</label>

                                <div class="col-md-8 col-lg-7">
                                    <file-upload
                                        :existing-file="user.profile_pic_url"
                                        id="avatar"
                                        module="Widget"
                                    ></file-upload>
                                </div>
                            </div>
                        </div>

                        <div class="form-section">
                            <div class="row mb-md-4">
                                <div class="col-md-auto offset-md-1">
                                    <h3 v-translate>Contact Information</h3>
                                </div>
                            </div>

                            <div class="form-group justify-content-center align-items-start form-row">
                                <label
                                    for="email"
                                    class="col-md-3"
                                    v-translate
                                >E-Mail</label>

                                <div class="col-md-8 col-lg-7">
                                    <Input
                                        name="email"
                                        type="email"
                                        errorType="email"
                                        model="user.email"
                                        module="Widget"
                                        :disabledCheck="!!user.edit_lock"
                                    />
                                </div>
                            </div>
                            <div class="form-group justify-content-center align-items-start form-row">
                                <label
                                    for="mobile"
                                    class="col-md-3"
                                    v-translate
                                >Phone</label>

                                <PhoneInput
                                    @change="setMobile"
                                    :value="mobile"
                                    wrapperClass="col-md-8 col-lg-7"
                                    :disabledCheck="!!user.edit_lock"
                                />
                            </div>
                        </div>

                        <div class="form-section">
                            <div class="row mb-md-4">
                                <div class="col-md-auto offset-md-1">
                                    <h3 v-translate>Home Address Information</h3>
                                </div>
                            </div>

                            <div class="form-group justify-content-center align-items-start form-row">
                                <label
                                    for="country"
                                    class="col-md-3"
                                    v-translate
                                >Country</label>

                                <div
                                    class="col-md-8 col-lg-7"
                                    v-if="countries.length"
                                >
                                    <CountrySelect
                                        :required="true"
                                        :disabled="formSubmitting || user.edit_lock"
                                        :options="countries"
                                        :hasValue="home_country ? home_country : false"
                                        :setCountry="setHomeCountry"
                                        :getIsoCodeFromName="getIsoCodeFromName"
                                        :label="'home_country'"
                                        :handler="countryHandler"
                                        :disabledCheck="!!user.edit_lock"
                                    >
                                        <template slot-scope="{slotKey}">
                                            <span
                                                :data-key="slotKey"
                                                :class="`iti-flag ${slotKey.toLowerCase()}`"
                                            ></span>&nbsp;
                                        </template>
                                    </CountrySelect>
                                    <InlineError
                                        :errors="errors"
                                        type="home_country"
                                    />
                                </div>
                                <div
                                    class="col-md-8 col-lg-7 loader loader--inline"
                                    v-else
                                >
                                    <loader />
                                </div>
                            </div>

                            <div class="form-group justify-content-center align-items-start form-row">
                                <label
                                    for="home_house_name"
                                    class="col-md-3"
                                    v-translate
                                >House Name</label>

                                <div class="col-md-8 col-lg-7">
                                    <Input
                                        id="home_house_name"
                                        name="home_house_name"
                                        :rule="{ rules: { required: user.home_house_number ? false : true } }"
                                        model="user.home_house_name"
                                        module="Widget"
                                    />
                                </div>
                            </div>
                            <div class="form-group justify-content-center align-items-start form-row">
                                <label
                                    for="home_house_number"
                                    class="col-md-3"
                                    v-translate
                                >House Number</label>

                                <div class="col-md-8 col-lg-7">
                                    <Input
                                        id="home_house_number"
                                        name="home_house_number"
                                        :rule="{ rules: { required: user.home_house_name ? false : true } }"
                                        model="user.home_house_number"
                                        module="Widget"
                                    />
                                </div>
                            </div>

                            <div class="form-group justify-content-center align-items-start form-row">
                                <label
                                    for="street_address"
                                    class="col-md-3"
                                    v-translate
                                >Street Address</label>

                                <div class="col-md-8 col-lg-7">
                                    <AutoCompleteInput
                                        id="street_address"
                                        name="home_street"
                                        model="user.home_street"
                                        module="Widget"
                                        :autocompleteOptions="predictions"
                                        :emulatePlaceChange="emulatePlaceChange"
                                        :changeHandler="callAutoComplete"
                                        :disabledCheck="!!user.edit_lock"
                                    />
                                </div>
                            </div>

                            <div class="form-group justify-content-center align-items-start form-row">
                                <label
                                    for="state"
                                    class="col-md-3"
                                    v-show="!showCounty()"
                                    v-translate
                                >State</label>
                                <label
                                    for="state"
                                    class="col-md-3"
                                    v-show="showCounty()"
                                    v-translate
                                >County</label>

                                <div class="col-md-8 col-lg-7">
                                    <Input
                                        :rule="'required'"
                                        validateAs="state"
                                        id="state"
                                        name="home_state"
                                        errorType="home_state"
                                        model="user.home_state"
                                        module="Widget"
                                        :required="true"
                                        :lazy="false"
                                        :disabledCheck="!!user.edit_lock"
                                    />
                                </div>
                            </div>

                            <div class="form-group justify-content-center align-items-start form-row">
                                <label
                                    for="city"
                                    class="col-md-3"
                                    v-translate
                                >City</label>

                                <div class="col-md-8 col-lg-7">
                                    <Input
                                        :rule="'required'"
                                        validateAs="city"
                                        id="city"
                                        name="home_city"
                                        errorType="home_city"
                                        model="user.home_city"
                                        module="Widget"
                                        :required="true"
                                        :lazy="false"
                                        :disabledCheck="!!user.edit_lock"
                                    />
                                </div>
                            </div>
                            <div class="form-group justify-content-center align-items-start form-row">
                                <label
                                    for="postcode"
                                    class="col-md-3"
                                    v-show="showPostcode()"
                                    v-translate
                                >Postcode</label>
                                <label
                                    for="postcode"
                                    class="col-md-3"
                                    v-show="!showPostcode()"
                                    v-translate
                                >Zipcode</label>

                                <div class="col-md-8 col-lg-7">
                                    <Input
                                        :rule="'required'"
                                        validateAs="postcode"
                                        id="postcode"
                                        name="home_postcode"
                                        errorType="home_postcode"
                                        model="user.home_postcode"
                                        module="Widget"
                                        :required="true"
                                        :lazy="false"
                                        :disabledCheck="!!user.edit_lock"
                                    />
                                </div>
                            </div>
                        </div>
                    </form>
                </div>

                <div class="card-footer">
                    <div class="row justify-content-center">
                        <div class="col-md-8">
                            <flash-message></flash-message>
                        </div>
                    </div>

                    <div class="form-group form-row justify-content-center">
                        <div class="col-12 col-sm-auto text-center ml-sm-auto">
                            <LoaderedButton
                                type="submit"
                                btnClasses="btn btn-primary"
                                :content="'Save' | translate"
                                :clickHandler="submitForm"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapFields } from 'vuex-map-fields';
import { mapActions, mapState, mapMutations } from 'vuex';
import FileUpload from '@C/FileUpload.vue';
import PhoneInput from '@C/PhoneInput';
import CustomSelect from '@C/CustomSelect';
import CountrySelect from '@C/CountrySelect';
import DOBPicker from '@C/DOBPicker';
import widgetMixin from '@M/widgets/dialog';
import { twoDigits, getDaysInMonth } from '@H/enrolment';
import { isDesktop, isMobile } from '@H/device_helpers';

const actions = {
    ...mapActions({
        querySalutations: 'Auth/querySalutations'
    }),
    ...mapActions('Widget', ['updateProfile', 'setLanguage']),
    ...mapActions(['getOrganisedCountries']),
    ...mapActions('Widget', ['resetHomeFields'])
};

const mutations = {
    ...mapMutations('Widget', ['setHomeCountry', 'setMobile', 'setDateOfBirth'])
};

const state = {
    ...mapState('Widget', ['user', '_widgetInstance']),
    ...mapState('Auth', ['allowedSalutations']),
    ...mapState('Flash', ['flash']),
    ...mapState(['countries'])
};

const fields = {
    ...mapFields('Widget', [
        'user.home_state',
        'user.home_house_name',
        'user.home_house_number',
        'user.home_street',
        'user.home_city',
        'user.home_postcode',
        'user.mobile',
        'user.experience',
        'user.home_country',
        'user.salutation'
    ])
};

export default {
    mixins: [widgetMixin],
    created() {
        this.getOrganisedCountries();
        this.querySalutations();
    },
    data() {
        return {
            day: 1,
            month: 1,
            year: 2000,
            days: 31,
            autocompleteIdentifier: 'home',
            months: [
                'January',
                'February',
                'March',
                'April',
                'May',
                'June',
                'July',
                'August',
                'September',
                'October',
                'November',
                'December'
            ]
        };
    },
    methods: {
        ...actions,
        ...mutations,
        formHandler() {
            this.setDateOfBirth(this.formattedDob);
            this.updateProfile();
        },
        showPostcode: function () {
            return (
                this.user.home_country == 'United Kingdom' ||
                typeof this.user.home_country == 'undefined'
            );
        },
        showState: function () {
            return this.user.home_country == 'United States';
        },
        addressFieldChanged: function () {
            if (typeof this.user.billing_address != 'undefined') {
                this.user.billing_address.same_as_home = false;
            }
        },
        /**
         * Update the profile picture field once a file has been uploaded successfully
         */
        avatarUploaded(file) {
            this.user.profile_pic_url = file.path;
        },
        countryHandler(e, firstRun = false) {
            let val = e.value || e.target.value;
            this.getIsoCodeFromName(val, this.countries);
            this.setHomeCountry(val);

            if (!firstRun) {
                this.resetHomeFields();
            }
        },
        showCounty() {
            return this.home_country == 'United Kingdom';
        },
        dobHandler(e) {
            let el = e.target || e;
            let key = this.stripDob(el.name);
            let val;
            if (key === 'month') {
                val = twoDigits(
                    this.months.indexOf(e.value || e.target.value) + 1
                );
            } else {
                val = e.value || e.target.value;
            }
            this[key] = val;

            const days = getDaysInMonth(this.month, this.year);

            this.$set(this.$data, 'days', days);
        },
        stripDob(str) {
            return str.split('-')[1];
        },
        languageHandler(e) {
            if (typeof e.target == 'undefined') return;
            let optionEl = e.target[e.target.selectedIndex];

            this.setLanguage(optionEl.getAttribute('data-key'));
        }
    },
    computed: {
        ...state,
        ...fields,
        formattedDob() {
            return `${this.year}-${this.month}-${twoDigits(this.day)}`;
        },
        currentLanguage() {
            return this.$language.available[this.user.language];
        },
        hasScroll() {
            return this.newLayout && isDesktop() ? 'perfect-scrollbar' : '';
        },
        isMobile() {
            return isMobile();
        }
    },
    watch: {
        allowedSalutations(val) {
            if (val) {
                this.$validator.extend('radioTabs', {
                    getMessage: field =>
                        `No value has been selected for ${field}.`,
                    validate: value => {
                        if (val !== '') {
                            return true;
                        } else {
                            return false;
                        }
                    }
                });
            }
        }
    },
    components: {
        FileUpload,
        PhoneInput,
        CustomSelect,
        DOBPicker
    }
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/stages/Widgets/EditProfile.vue